// src/pages/productos/Productos.js
import React, { useEffect, useState } from 'react'
import Container from 'react-bootstrap/Container'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import InputGroup from 'react-bootstrap/InputGroup'
import Button from 'react-bootstrap/Button'
import Table from 'react-bootstrap/Table'
import Badge from 'react-bootstrap/Badge'
import { toast } from 'react-toastify'
import api from 'api/api'

import { PRODUCTOS_FETCH_CUANTOS, PRODUCTOS_NO_IMAGE } from 'config/productos'
import { debounce } from 'utils/debounce'
import Paginacion from 'components/Paginacion'
import { LoadingSpinner } from 'components/LoadingSpinner'
import NuevoProducto from './NuevoProducto'
import EditarProducto from './EditarProducto'
import EliminarProducto from './EliminarProducto'
import VerProducto from './VerProducto'
import TopHeader from 'components/TopHeader'

import './productos.css'
import { API_UPLOADS_URL } from 'config/general'
import { formatNumberForDisplay } from 'utils/numberUtils'

function Productos() {
	const [fetchDeACuantos, setFetchDeACuantos] = useState(PRODUCTOS_FETCH_CUANTOS)
	const [numResultados, setNumResultados] = useState(0)
	const [paginaAct, setPaginaAct] = useState(1)
	const [loading, setLoading] = useState(false)

	const [busqueda, setBusqueda] = useState('')
	const [filtroBusqueda, setFiltroBusqueda] = useState('')
	const [productos, setProductos] = useState([])
	const [categorias, setCategorias] = useState([])
	const [filtroCategoria, setFiltroCategoria] = useState('')
	const [filtroDestacado, setFiltroDestacado] = useState(false)
	const [filtroPromocion, setFiltroPromocion] = useState(false)

	const [accionActual, setAccionActual] = useState({
		producto: null,
		accion: ''
	})

	const opcionesCuantosMostrar = [10, 25, 50]

	// Funciones auxiliares para normalizar estados booleanos
	const esDestacado = (producto) => {
		return producto.destacado === '1' || producto.destacado === 1 || producto.destacado === true
	}

	const esActivo = (producto) => {
		return producto.activo === '1' || producto.activo === 1 || producto.activo === true
	}

	const refreshCategorias = async () => {
		try {
			const { datos } = await api.get('a/config-jerarquias')
			setCategorias(datos.jerarquias || [])
		} catch (error) {
			toast.error('Error al cargar las categorías')
			console.error(error)
		}
	}

	const refreshProductos = async () => {
		try {
			setLoading(true)

			// Construimos los filtros en base a las selecciones
			const filtros = {
				busqueda: [filtroBusqueda]
			}

			// Agregar filtro por categoría si está seleccionada
			if (filtroCategoria) {
				filtros.jerarquiaId = filtroCategoria
			}

			// Agregar filtro por destacado si está seleccionado
			if (filtroDestacado) {
				filtros.destacado = true
			}

			// Agregar filtro por promoción si está seleccionado
			if (filtroPromocion) {
				filtros.enPromocion = true
			}

			const { datos } = await api.post('a/ver-items', {
				cuantos: fetchDeACuantos,
				desde: paginaAct === 1 ? 0 : (paginaAct - 1) * fetchDeACuantos,
				filtros
			})

			setProductos(datos.items)
			setNumResultados(datos.numResultados)
		} catch (error) {
			toast.error('Error de conexión')
			console.error(error)
		} finally {
			setLoading(false)
		}
	}

	const handleBusqueda = (value) => {
		if (value) {
			setFiltroBusqueda(value)
		} else {
			setFiltroBusqueda('')
		}
	}

	const debouceFiltroBusqueda = React.useCallback(
		debounce(handleBusqueda, 400),
		[]
	)

	useEffect(() => {
		debouceFiltroBusqueda(busqueda)
	}, [busqueda])

	useEffect(() => {
		if (paginaAct === 1) {
			refreshProductos()
		} else {
			setPaginaAct(1)
		}
	}, [filtroBusqueda, fetchDeACuantos, filtroCategoria, filtroDestacado, filtroPromocion])

	useEffect(() => {
		if (!loading) {
			refreshProductos()
		}
	}, [paginaAct])

	useEffect(() => {
		refreshCategorias()
		refreshProductos()
	}, [])

	const nuevoProducto = async (data) => {
		try {
			const { msg, datos } = await api.post('a/items', data)
			refreshProductos()
			cerrarAccion()
			toast.success(msg)
			return datos
		} catch (error) {
			console.error(error)
			toast.error('Error al crear el producto')
		}
	}

	const editarProducto = async (data) => {
		try {
			const { msg } = await api.put('a/items/' + accionActual.producto.id, data)
			refreshProductos()
			cerrarAccion()
			toast.success(msg)
		} catch (error) {
			console.error(error)
			toast.error('Error al actualizar el producto')
		}
	}

	const eliminarProducto = async (id) => {
		try {
			const { msg } = await api.delete('a/items/' + id)
			refreshProductos()
			cerrarAccion()
			toast.success(msg)
		} catch (error) {
			console.error(error)
			toast.error('Error al eliminar el producto')
		}
	}

	const formatearPrecio = (precio) => {
		const precioFormateado = formatNumberForDisplay(precio)
		if (precioFormateado === '0') return 'Sin precio'
		return precioFormateado
	}

	// Acciones para manejar los productos
	const accionesActuales = {
		handleVer: (producto) => {
			setAccionActual({
				producto,
				accion: 'ver'
			})
		},
		handleEditar: (producto) => {
			setAccionActual({
				producto,
				accion: 'editar'
			})
		},
		handleEliminar: (producto) => {
			setAccionActual({
				producto,
				accion: 'eliminar'
			})
		},
	}

	const cerrarAccion = () => {
		setAccionActual({
			producto: null,
			accion: ''
		})
	}

	// render
	return (
		<Container fluid id="productos" className="vh-100">
			<TopHeader
				iconName="boxes"
				text="Productos"
			/>

			{/* Filtros */}
			<Row className="d-flex no-gutters mb-3">
				<Col className="mt-2">
					<Form>
						<Row id="searchbar" className="justify-content-between align-items-center g-2">
							<Col sm={6} md={2} style={{ display: 'block' }}>
								<small>
									<span className="d-none d-sm-inline-block">Mostrar de a</span>
									<Form.Select size="sm" onChange={(e) => setFetchDeACuantos(e.target.value)} className="mx-1">
										{opcionesCuantosMostrar.map((o) => (
											<option value={o} key={`mostrar-${o}`}>
												{o}
											</option>
										))}
									</Form.Select>
									<span className="ms-2">productos</span>
								</small>
							</Col>

							<Col md={4} lg={3}>
								<Form.Group controlId="txtBusqueda">
									<InputGroup>
										<Form.Control
											autoFocus
											value={busqueda}
											onChange={(e) => setBusqueda(e.target.value)}
											type="text"
											placeholder="Buscar por nombre o código"
											aria-label="Buscar"
										/>
										<InputGroup.Text><i className="fa fa-search"></i></InputGroup.Text>
									</InputGroup>
								</Form.Group>
							</Col>

							<Col md={3} lg={2}>
								<Form.Group controlId="selectCategoria">
									<Form.Select
										value={filtroCategoria}
										onChange={(e) => setFiltroCategoria(e.target.value)}
									>
										<option value="">Todas las categorías</option>
										{categorias.map(categoria => (
											<option key={categoria.id} value={categoria.id}>
												{categoria.nombre}
											</option>
										))}
									</Form.Select>
								</Form.Group>
							</Col>

							<Col md={3} lg={2}>
								<Form.Check
									type="switch"
									id="checkDestacados"
									label="Solo destacados"
									checked={filtroDestacado}
									onChange={(e) => setFiltroDestacado(e.target.checked)}
									className="mb-2"
								/>
								<Form.Check
									type="switch"
									id="checkPromocion"
									label="Solo en promoción"
									checked={filtroPromocion}
									onChange={(e) => setFiltroPromocion(e.target.checked)}
								/>
							</Col>
						</Row>
					</Form>
				</Col>
			</Row>

			{/* Botón para Nuevo producto */}
			<Row className="mb-3">
				<Col>
					<Button
						variant="primary"
						onClick={() => setAccionActual({
							producto: null,
							accion: 'nuevo'
						})}
					>
						<i className="fa fa-plus"></i> Nuevo producto
					</Button>
				</Col>
			</Row>

			{/* Tabla de productos */}
			<Row>
				<Col>
					<div className="productos-tabla">
						<small>Total resultados: {loading ? (<span className="ms-2"><LoadingSpinner size='sm' /></span>) : (<strong>{numResultados}</strong>)}</small>

						<Table striped bordered hover responsive className="mt-2">
							<thead>
								<tr>
									<th>Imagen</th>
									<th>Nombre</th>
									<th>Código</th>
									<th>Categoría(s)</th>
									<th>Precio</th>
									<th>Estado</th>
									<th>Orden</th>
									<th style={{ width: 140 }}>Acciones</th>
								</tr>
							</thead>
							<tbody>
								{loading ? (
									<tr>
										<td colSpan="7" className="text-center py-4">
											<LoadingSpinner />
										</td>
									</tr>
								) : (
									productos.length > 0 ? productos.map(producto => {
										const imageUrl = producto.multimedia && producto.multimedia.length > 0
											? API_UPLOADS_URL + producto.multimedia[0].url
											: PRODUCTOS_NO_IMAGE

										return (
											<tr key={producto.id}>
												<td className="text-center align-middle">
													<div className="imagen-container">
														<img
															src={imageUrl}
															alt={producto.nombre || 'Imagen de producto'}
															className="producto-imagen"
														/>
													</div>
												</td>
												<td>
													{esDestacado(producto) &&
														<Badge bg="warning" className="me-1 badge-destacado">
															<i className="fa fa-star"></i>
														</Badge>
													}
													{producto.nombre}
												</td>
												<td>{producto.codigo || '-'}</td>
												<td>
													{producto.jerarquias && producto.jerarquias.map((cat) => (
														<Badge bg="secondary" key={cat.id} className="me-1">
															{cat.nombre}
														</Badge>
													))}
												</td>
												<td>
													{producto.precioPromocion ? (
														<>
															<div className="precio-original">
																{formatearPrecio(producto.precio)}
															</div>
															<div className="precio-promocion">
																<Badge pill bg="danger" className="me-1 badge-promocion">
																	<i className="fa fa-tag"></i>
																</Badge>
																{formatearPrecio(producto.precioPromocion)}
															</div>
														</>
													) : (
														formatearPrecio(producto.precio)
													)}
												</td>
												<td className="text-center">
													{esActivo(producto) ? (
														<Badge bg="success">Activo</Badge>
													) : (
														<Badge bg="secondary">Inactivo</Badge>
													)}
												</td>
												<td>
													{producto.orden}
												</td>
												<td className="text-center">
													<Button
														variant="outline-info"
														size="sm"
														className="me-1"
														onClick={() => accionesActuales.handleVer(producto)}
													>
														<i className="fa fa-eye"></i>
													</Button>
													<Button
														variant="outline-primary"
														size="sm"
														className="me-1"
														onClick={() => accionesActuales.handleEditar(producto)}
													>
														<i className="fa fa-pencil"></i>
													</Button>
													<Button
														variant="outline-danger"
														size="sm"
														onClick={() => accionesActuales.handleEliminar(producto)}
													>
														<i className="fa fa-trash"></i>
													</Button>
												</td>
											</tr>
										)
									}) : (
										<tr>
											<td colSpan="7" className="text-center py-4">
												No se encontraron productos con los filtros aplicados.
											</td>
										</tr>
									)
								)}
							</tbody>
						</Table>

						{/* Paginación */}
						{productos.length > 0 && (
							<Paginacion
								pagina={paginaAct}
								numPaginas={Math.ceil(numResultados / fetchDeACuantos) || 1}
								setPagina={setPaginaAct}
							/>
						)}
					</div>
				</Col>
			</Row>

			{/* Paneles para acciones CRUD */}
			<NuevoProducto
				mostrar={accionActual.accion === 'nuevo'}
				guardar={nuevoProducto}
				cancelar={cerrarAccion}
				categorias={categorias}
			/>

			<VerProducto
				mostrar={accionActual.accion === 'ver'}
				producto={accionActual.producto}
				cancelar={cerrarAccion}
			/>

			<EditarProducto
				mostrar={accionActual.accion === 'editar'}
				productoOriginal={accionActual.producto}
				cancelar={cerrarAccion}
				guardar={editarProducto}
				categorias={categorias}
			/>

			<EliminarProducto
				mostrar={accionActual.accion === 'eliminar'}
				producto={accionActual.producto}
				cancelar={cerrarAccion}
				eliminar={eliminarProducto}
			/>
		</Container>
	)
}

export default Productos